import {MarkdownRenderer} from '@github-ui/copilot-markdown'
import {NewToolHeader, type ToolHeaderPropsType} from './NewToolHeader'
import toolCodeBlockExtension, {type ToolCodeBlockExtensionArgs} from './code-block/ToolCodeBlockExtension'
import type {ReactNode} from 'react'
import {Children, createContext, use, useEffect, useRef, useState} from 'react'
import {SkeletonText} from '@primer/react/experimental'
import styles from './Tool.module.css'
import {clsx} from 'clsx'
import {AnimatedCollapse} from '../../AnimatedCollapse'
import {useSessionLogsContext} from '../Context'
import {ToolFooterSlotContext, useToolFooterSlot} from './ToolFooterSlot'
import footerStyles from './ToolFooterSlot.module.css'

const MAX_TOOL_CONTENT_LENGTH = 100_000
const SCROLLABLE_TOOL_OUTPUT_LINE_THRESHOLD = 12
const SCROLLABLE_TOOL_OUTPUT_CHARACTER_THRESHOLD = 2_000
// Height threshold for tool content preview - content shorter than this will be shown fully expanded
// Set as a CSS custom property via inline style so both JS and CSS reference the same value
const TOOL_PREVIEW_HEIGHT_THRESHOLD = 475

export const wrapInCodeBlock = ({language, markdown}: {language: string; markdown: string}) => {
  if (!markdown) {
    return `~~~\nNothing to display\n~~~`
  }

  // Use ~~~ for the delimiter to avoid conflicts with backticks. This is acceptable by remark-gfm, the underlying rendering library.
  return `~~~${language}\n${markdown}\n~~~`
}

/**
 * Truncates markdown content to prevent the markdown parser from performance issues.
 * Returns the truncated content and a flag indicating if truncation occurred.
 * If truncation fails, returns the original markdown.
 */
export function truncateMarkdownContent(
  markdown: string | undefined,
  maxLength: number = MAX_TOOL_CONTENT_LENGTH,
): {markdown: string | undefined} {
  if (!markdown || markdown.length <= maxLength) {
    return {markdown}
  }

  const truncated = markdown.slice(0, maxLength)
  const truncationMessage = `\n\n⚠️ **Output truncated**`

  return {
    markdown: truncated + truncationMessage,
  }
}

export const ToolDefaultOpenContext = createContext<boolean | undefined>(undefined)

export function shouldWrapInCodeBlock(language: string): boolean {
  return !(language === 'markdown' || language === 'text' || language === '')
}

export type ToolDisclosureMemory = {read: () => boolean | undefined; write: (open: boolean) => void}

export const ToolDisclosureMemoryContext = createContext<ToolDisclosureMemory | undefined>(undefined)

export function createToolDisclosureMemoryStore() {
  const openByKey = new Map<string, boolean>()
  const memoryByKey = new Map<string, ToolDisclosureMemory>()
  return {
    memoryFor: (key: string): ToolDisclosureMemory => {
      let memory = memoryByKey.get(key)
      if (!memory) {
        memory = {read: () => openByKey.get(key), write: open => void openByKey.set(key, open)}
        memoryByKey.set(key, memory)
      }
      return memory
    },
    writeAll: (open: boolean) => {
      for (const key of memoryByKey.keys()) openByKey.set(key, open)
    },
  }
}

export function Tool({
  title,
  isError = false,
  isPending = false,
  path,
  hasPreview = false,
  collapseLargeContent = false,
  collapsible = true,
  bordered = true,
  toolHeaderProps,
  showTitleSuffixWhenCollapsedOnly = false,
  inlineTitleSuffix = false,
  children,
  isOpenOverride,
  isInGroup = false,
  keepContentMounted = false,
}: {
  title: string
  isError: boolean
  isPending?: boolean
  path?: string
  hasPreview?: boolean
  collapseLargeContent?: boolean
  /** When false, content is always visible and the header has no toggle affordance */
  collapsible?: boolean
  /** When false, the tool renders without a border/background container */
  bordered?: boolean
  toolHeaderProps?: ToolHeaderPropsType
  showTitleSuffixWhenCollapsedOnly?: boolean
  inlineTitleSuffix?: boolean
  children: ReactNode
  isOpenOverride?: boolean
  isInGroup?: boolean
  keepContentMounted?: boolean
}) {
  const {variant} = useSessionLogsContext()
  const footer = useToolFooterSlot()
  const defaultOpen = use(ToolDefaultOpenContext)
  const openOverride = isOpenOverride ?? defaultOpen

  // Use isOpenOverride as the initial value, but allow user to toggle manually
  const disclosureMemory = use(ToolDisclosureMemoryContext)
  const [rememberedOpen] = useState(() => (collapsible ? disclosureMemory?.read() : undefined))
  const hasUserToggledRef = useRef(rememberedOpen !== undefined)
  const [isToolContentOpen, setIsToolContentOpen] = useState(
    collapsible ? (rememberedOpen ?? openOverride ?? false) : true,
  )
  const prevIsOpenOverrideRef = useRef(openOverride)
  const toolRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [shouldShowPreview, setShouldShowPreview] = useState(hasPreview)

  // Sync with isOpenOverride changes, but only if user hasn't manually toggled
  useEffect(() => {
    if (prevIsOpenOverrideRef.current !== openOverride) {
      prevIsOpenOverrideRef.current = openOverride
      // Only apply isOpenOverride if user hasn't manually toggled
      if (!hasUserToggledRef.current && openOverride !== undefined) {
        if (defaultOpen !== undefined && !openOverride && contentRef.current?.contains(document.activeElement)) {
          toolRef.current?.querySelector<HTMLButtonElement>('button[aria-expanded]')?.focus()
        }
        setIsToolContentOpen(openOverride)
      }
    }
  }, [defaultOpen, openOverride])

  // Check content height to determine if preview should be shown
  useEffect(() => {
    if (!hasPreview || !contentRef.current) {
      setShouldShowPreview(hasPreview)
      return
    }

    // Check content height after render
    const contentHeight = contentRef.current.scrollHeight
    // If content is less than threshold, show it fully expanded by default
    if (contentHeight < TOOL_PREVIEW_HEIGHT_THRESHOLD) {
      setShouldShowPreview(false)
      // Auto-expand if content is small
      if (!hasUserToggledRef.current) {
        setIsToolContentOpen(true)
      }
    } else {
      setShouldShowPreview(!collapseLargeContent)
      if (collapseLargeContent && !hasUserToggledRef.current) {
        setIsToolContentOpen(false)
      }
    }
  }, [hasPreview, collapseLargeContent])

  const toolInstanceRef = useRef({})

  // Listen for global expand/collapse events
  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{expand: boolean; excludeInstance: object}>
      if (customEvent.detail.excludeInstance !== toolInstanceRef.current) {
        if (disclosureMemory || defaultOpen !== undefined) hasUserToggledRef.current = true
        disclosureMemory?.write(customEvent.detail.expand)
        // Update React state
        setIsToolContentOpen(customEvent.detail.expand)
      }
    }

    document.addEventListener('mission-control-tool-global-toggle', handler)
    return () => {
      document.removeEventListener('mission-control-tool-global-toggle', handler)
    }
  }, [defaultOpen, disclosureMemory])

  const handleToggle = () => {
    if (!collapsible) return
    // Mark that user has manually toggled
    hasUserToggledRef.current = true
    disclosureMemory?.write(!isToolContentOpen)
    setIsToolContentOpen(!isToolContentOpen)
  }
  const handleModifierClick = (expand: boolean) => {
    if (!collapsible) return
    // Mark that user has manually toggled
    hasUserToggledRef.current = true
    disclosureMemory?.write(expand)

    // Update React state
    setIsToolContentOpen(expand)

    // Dispatch event to all other tools
    const event = new CustomEvent('mission-control-tool-global-toggle', {
      detail: {expand, excludeInstance: toolInstanceRef.current},
    })
    document.dispatchEvent(event)
  }

  // Check if there are any meaningful children to determine if we have references
  // Filter out null, undefined, false, and empty values
  const hasReferences = collapsible && Children.toArray(children).filter(Boolean).length > 0

  // The footer belongs to this frame only, so nested frames must not render it again.
  const content = (
    <ToolDisclosureMemoryContext value={undefined}>
      {footer && bordered ? (
        <>
          <ToolFooterSlotContext value={undefined}>{children}</ToolFooterSlotContext>
          <div className={footerStyles.toolFooterSlot}>{footer}</div>
        </>
      ) : (
        children
      )}
    </ToolDisclosureMemoryContext>
  )

  return (
    <div
      ref={toolRef}
      className={clsx(styles.toolContainer, {
        [styles.toolContainerDefault]: bordered && variant === 'default',
        [styles.toolContainerBorderless]: !bordered,
        [styles.toolContainerInGroup]: isInGroup,
        [styles.toolContainerExpanded]: isToolContentOpen,
      })}
      style={{'--tool-preview-height-threshold': `${TOOL_PREVIEW_HEIGHT_THRESHOLD}px`}}
    >
      <NewToolHeader
        leadingVisual={toolHeaderProps?.leadingVisual}
        trailingVisual={toolHeaderProps?.trailingVisual}
        titleSuffix={showTitleSuffixWhenCollapsedOnly && isToolContentOpen ? undefined : toolHeaderProps?.titleSuffix}
        inlineTitleSuffix={inlineTitleSuffix || toolHeaderProps?.inlineTitleSuffix}
        title={title}
        path={path}
        isCollapsed={!isToolContentOpen}
        isPending={isPending}
        isError={isError}
        isInGroup={isInGroup}
        onToggle={handleToggle}
        onModifierClick={handleModifierClick}
        hasReferences={hasReferences}
      />
      {shouldShowPreview ? (
        <div
          ref={contentRef}
          className={clsx(
            {
              [styles.toolContentPreview]: !isToolContentOpen,
              [styles.toolContentMissionControl]: bordered && isToolContentOpen && variant === 'mission-control',
              [styles.toolContentBorderless]: !bordered && isToolContentOpen,
            },
            bordered && isToolContentOpen && variant === 'default' && 'border-top',
            bordered && isToolContentOpen && variant === 'default' && styles.toolContent,
          )}
          {...(!isToolContentOpen ? {inert: true} : {})}
        >
          {content}
        </div>
      ) : (
        <AnimatedCollapse open={isToolContentOpen} keepMounted={keepContentMounted}>
          <div
            ref={contentRef}
            className={clsx(
              {
                [styles.toolContentMissionControl]: bordered && variant === 'mission-control',
                [styles.toolContentBorderless]: !bordered,
              },
              bordered && variant === 'default' && 'border-top',
              bordered && variant === 'default' && styles.toolContent,
            )}
          >
            {content}
          </div>
        </AnimatedCollapse>
      )}
    </div>
  )
}

export function ToolPrimaryContent({
  language,
  markdown,
  ...extensionProps
}: {
  language: string
  markdown: string | undefined
} & ToolCodeBlockExtensionArgs) {
  const {markdown: truncatedMarkdown} = truncateMarkdownContent(markdown)

  return markdown === undefined ? (
    <MarkdownLoading />
  ) : (
    <MarkdownRenderer
      markdown={
        shouldWrapInCodeBlock(language)
          ? wrapInCodeBlock({language, markdown: truncatedMarkdown || ''})
          : truncatedMarkdown || ''
      }
      extensions={[toolCodeBlockExtension({...extensionProps})]}
      // Code gets rendered with padding so we need to apply padding to content not being wrapped in a code block
      className={clsx(!shouldWrapInCodeBlock(language) && 'tmp-p-3')}
    />
  )
}

export function ToolCodeOutput({output}: {output: string | undefined}) {
  if (output === undefined) return <MarkdownLoading />

  const truncatedOutput =
    output.length > MAX_TOOL_CONTENT_LENGTH
      ? `${output.slice(0, MAX_TOOL_CONTENT_LENGTH)}\n\n⚠️ Output truncated`
      : output
  const isScrollable =
    output.length > SCROLLABLE_TOOL_OUTPUT_CHARACTER_THRESHOLD ||
    output.split(/\r?\n/).length > SCROLLABLE_TOOL_OUTPUT_LINE_THRESHOLD

  return (
    <pre
      className={clsx(styles.toolCodeOutput, isScrollable && styles.toolCodeOutputScrollable)}
      {...(isScrollable ? {tabIndex: 0} : {})}
    >
      {truncatedOutput}
    </pre>
  )
}

export function MarkdownLoading({lines = 3, maxWidth = '45ch'}: {lines?: number; maxWidth?: string}) {
  return (
    // Padding matches the default padding of the MarkdownRenderer
    <div className="tmp-py-3 tmp-px-4">
      <SkeletonText lines={lines} maxWidth={maxWidth} />
    </div>
  )
}

// Parses the delta.content for <error> tags to detect errors and remove tags from content
export function parseContent(content?: string): {isError: boolean; content: string | undefined} {
  if (!content) return {isError: false, content}

  const startTag = `<${'error'}>`
  const endTag = `</${'error'}>`
  const startIndex = content.indexOf(startTag)
  const endIndex = startIndex !== -1 ? content.indexOf(endTag, startIndex + startTag.length) : -1
  const match = startIndex !== -1 && endIndex !== -1 ? content.slice(startIndex + startTag.length, endIndex) : null

  if (match !== null) {
    return {
      isError: true,
      content: match,
    }
  }

  return {isError: false, content}
}
